import { Role, useGetAppSelectorInfo, useGetUserInfo } from 'auth';
import classNames from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { CURRENT_USER_QUERY_KEY, useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import {
  DATE_RANGE_SEARCH_PARAM_KEY,
  ENTITY_ID_SEARCH_PARAM_KEY,
  ENTITY_IS_VERIFIED_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { useSidebarProfileMenuOptions } from '@/modules/common/hooks/useSidebarProfileMenuOptions.ts';
import { queryClient } from '@/modules/common/query.ts';
import { EntityTypeFixed } from '@/modules/entity/components/items/EntityListItem.tsx';
import {
  CURRENT_ENTITY_QUERY_KEY,
  useEntityItemQuery,
  useEntityItemUncachedQuery,
} from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { ProfileEntityMenu } from '@/modules/investor-dashboard/components/ProfileEntityMenu.tsx';
import { CompleteKYCModal } from '@/modules/investor-dashboard/components/modals/CompleteKYCModal.tsx';
import EditEntityModal, {
  EditEntityTabItem,
} from '@/modules/investor-dashboard/components/modals/EditEntityModal.tsx';
import { useSidebarHeaderMenuOptions } from '@/modules/investor-dashboard/hooks/useSidebarHeaderMenuOptions.tsx';
import { useSidebarOptions } from '@/modules/investor-dashboard/hooks/useSidebarOptions.ts';
import { SHOW_MODAL_COMPLETE_KYC } from '@/modules/investor-dashboard/queries/entity.ts';
import { useVerifiedEntityPaymentInfoMutation } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { useCurrentEntityShareholdersQuery } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { investorDashboardPaths } from '@/modules/investor-dashboard/routes.tsx';
import { ModalProviderContext, Sidebar, Spinner, TopBar, getSelectValueByEnum } from 'ui';
import { LogoSrWhite } from 'ui/icons/logo';
import { EntitiesPaymentInfoStatus, EntityStatus } from 'api';

export interface InvestorDashboardWrapperOutletContext {
  register_id: string;
  date_range: string;
  entity_id: string;
}

const InvestorDashboardWrapper = () => {
  const [params, setParams] = useSearchParams();
  const { avatar } = useGetUserInfo();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const openVerify = useRef(false);
  const openKycInfo = useRef(false);
  const entity_id = params.get(ENTITY_ID_SEARCH_PARAM_KEY);
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const date_range = params.get(DATE_RANGE_SEARCH_PARAM_KEY);

  const [registerIdParam, setRegisterIdParam] = useState(String(register_id));
  const [entityIdParam, setEntityIdParam] = useState(Number(entity_id));
  const [dateRangeParam, setDateRangeParam] = useState(date_range || 'all');

  const { data: currentUser } = useCurrentUserQuery();
  const entityIsVerified = String(params.get(ENTITY_IS_VERIFIED_KEY));
  const { mutateAsync: VerifiedEntityPaymentInfo } = useVerifiedEntityPaymentInfoMutation();
  const { openModal } = useContext(ModalProviderContext);
  const showModalCompleteKyc =
    queryClient.getQueryData<boolean>([SHOW_MODAL_COMPLETE_KYC]) ?? false;

  const {
    data: entityResponse,
    refetch: refetchEntityItem,
    isLoading: isEntityItemLoading,
  } = useEntityItemQuery(Number(entity_id));
  const { error } = useEntityItemUncachedQuery(Number(entity_id));
  const {
    isLoading: isShareholdersLoading,
    data: entityShareholdersResponse,
    refetch: refetchEntityShareholders,
  } = useCurrentEntityShareholdersQuery(Number(entity_id));
  const menuOptions = useSidebarOptions();
  const profileOptions = useSidebarProfileMenuOptions();
  const headerOptions = useSidebarHeaderMenuOptions(entityShareholdersResponse);
  const appSelectorInfo = useGetAppSelectorInfo({
    onClickBeforeChangeRole: () => {
      const currentUser = queryClient.getQueryData([CURRENT_USER_QUERY_KEY]);
      queryClient.clear();
      queryClient.setQueryData([CURRENT_USER_QUERY_KEY], currentUser);
    },
  });

  useEffect(() => {
    if (!entity_id || !register_id) {
      navigate(entityPaths.static.entityList);
      return;
    }
    if (+entity_id !== Number(entityIdParam)) {
      setEntityIdParam(+entity_id);
      refetchEntityItem();
      refetchEntityShareholders();
    }
    if (register_id !== String(registerIdParam)) {
      setRegisterIdParam(String(register_id));
    }
    if (date_range !== dateRangeParam && date_range) {
      setDateRangeParam(date_range);
    }
  }, [date_range, entity_id, register_id]);

  useEffect(() => {
    const errorMessage = (error as any)?.response?.data?.error_message as string;
    const errorCode = (error as any)?.response?.status as number;
    if (String(errorMessage) === 'You are not owner of this holder!' || errorCode === 403) {
      toast.error('You are not owner of this entity!', {
        id: 'access-denied',
        duration: 10000,
      });
      navigate(entityPaths.static.entityList);
      queryClient.removeQueries([CURRENT_ENTITY_QUERY_KEY]);
    }
  }, [error]);

  useEffect(() => {
    if (
      entityIsVerified &&
      entityResponse &&
      entityResponse.entity?.payment_info_status === EntitiesPaymentInfoStatus.Unverified &&
      Number(entity_id) === entityResponse.entity?.id
    ) {
      if (!openVerify.current) {
        openVerify.current = true;
        if (entityIsVerified === 'false') {
          openModal(EditEntityModal, {
            entity: entityResponse.entity,
            defaultTab: EditEntityTabItem.PaymentInstructions,
            onSuccess: () => {
              params.delete(ENTITY_IS_VERIFIED_KEY);
              // setParams(params);
            },
          });
          return;
        }
        if (entityIsVerified === 'true') {
          params.delete(ENTITY_IS_VERIFIED_KEY);
          // setParams(params);
          VerifiedEntityPaymentInfo();
          return;
        }
      }
    }
  }, [
    VerifiedEntityPaymentInfo,
    entityIsVerified,
    entityResponse,
    entity_id,
    openModal,
    params,
    setParams,
  ]);

  useEffect(() => {
    if (
      // !showModalCompleteKyc &&
      entityResponse &&
      entityResponse.entity?.status !== EntityStatus.EntityApproved &&
      entityResponse.entity?.status !== EntityStatus.EntityAwaitingApproval &&
      Number(entity_id) === entityResponse.entity?.id
    ) {
      if (!openKycInfo.current) {
        openKycInfo.current = true;
        openModal(CompleteKYCModal, {
          entityId: entityResponse.entity?.id,
        });
      }
    }
  }, [entityResponse, entity_id, openModal, showModalCompleteKyc]);

  useEffect(() => {
    const registers = entityShareholdersResponse?.shareholders || [];

    if (
      registers.length === 1 &&
      registers?.[0]?.register_id &&
      register_id === 'all' &&
      pathname?.includes(investorDashboardPaths.static.dashboard)
    ) {
      const new_register_id = String(registers[0].register_id);
      params.set(REGISTER_ID_SEARCH_PARAM_KEY, new_register_id);
      setParams(params);
      setRegisterIdParam(String(new_register_id));
    }
  }, [entityShareholdersResponse?.shareholders, params, pathname, register_id]);

  return (
    <div
      className={classNames(
        'bg-gray-900 sm:h-screen w-full flex sm:flex-row flex-col h-[calc(100%-56px)]',
      )}
    >
      <TopBar
        label={
          <Link
            to={{ pathname: investorDashboardPaths.static.dashboard, search: params.toString() }}
          >
            <img src={LogoSrWhite} alt="logo" className="h-8" />
          </Link>
        }
        avatarUrl={avatar}
        appSelectorInfo={appSelectorInfo}
        menuItems={menuOptions}
        profileMenuItems={profileOptions}
        headerMenuItems={headerOptions}
        headerMenuSelectedValue={
          headerOptions.length > 1
            ? params.get(REGISTER_ID_SEARCH_PARAM_KEY) || ''
            : headerOptions[0].value
        }
        header="menu"
        entity={entityResponse?.entity}
        user={currentUser}
        profileHeaderMenu={
          <ProfileEntityMenu placement="top-end" entity_type={entityResponse?.entity.type} />
        }
      />
      <Sidebar
        label={
          <Link
            to={{ pathname: investorDashboardPaths.static.dashboard, search: params.toString() }}
          >
            <img src={LogoSrWhite} alt="logo" className="h-8" />
          </Link>
        }
        avatarUrl={avatar}
        appSelectorInfo={appSelectorInfo}
        menuItems={menuOptions}
        profileMenuItems={profileOptions}
        headerMenuItems={headerOptions}
        isHeaderMenuItemsLoading={isShareholdersLoading}
        headerMenuSelectedItem={
          headerOptions.length > 1
            ? headerOptions.find(({ value }) => value === params.get(REGISTER_ID_SEARCH_PARAM_KEY))
            : headerOptions[0]
        }
        user={currentUser}
        entity={{
          ...entityResponse?.entity,
          type: getSelectValueByEnum<EntityTypeFixed>(
            entityResponse?.entity?.type ?? '',
            EntityTypeFixed,
          ).label,
        }}
        profileHeaderMenu={<ProfileEntityMenu entity_type={entityResponse?.entity.type} />}
      />

      <main
        className={classNames(
          'overflow-auto flex flex-col w-full sm:h-full h-[calc(100%-56px)] sm:flex-grow-0 flex-grow relative top-0 bottom-0',
          'bg-white justify-center rounded-tl-xl rounded-tr-xl sm:rounded-tr-none sm:rounded-bl-xl rounded-bl-none',
          {
            //loading
            'items-center':
              isEntityItemLoading || isShareholdersLoading || !entityResponse?.entity.id,
            'items-start': !(
              //not loading
              (isEntityItemLoading || isShareholdersLoading || !entityResponse?.entity.id)
            ),
          },
        )}
      >
        {isEntityItemLoading || isShareholdersLoading || !entityResponse?.entity.id ? (
          <Spinner size="lg" />
        ) : (
          <Outlet
            context={
              {
                register_id: registerIdParam,
                date_range: dateRangeParam,
                entity_id: String(entityIdParam),
              } as InvestorDashboardWrapperOutletContext
            }
          />
        )}
      </main>
    </div>
  );
};

export default withPrivateAccess(InvestorDashboardWrapper, Role.SR_INVESTOR);
